import React, { useState, useEffect, useRef } from 'react';
import { useAlert } from './CustomAlert/AlertContext';
import { useParams, useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import useScreenshotCapture from '../hooks/useScreenshotCapture';
import { getAiInterviewScreenshotIntervalMs, API_BASE } from '../utils/config';
import LinkExpiredView from './LinkExpiredView';
import logo from '../assets/logo.png';
import aiLogo from '../assets/ai-logo.png';

const VERIFICATION_PROMPTS = [
  { text: "I am participating in this autonomous AI interview and agree to provide honest, original answers. I understand that my camera and microphone are being actively monitored.", keyphrase: "actively monitored" },
  { text: "As a candidate for this position, I confirm that I am taking this test independently without using any external resources, conversational AI, or assistance from others.", keyphrase: "assistance from others" },
  { text: "I am ready to begin my technical assessment. I verify that this is my own work and that my face will remain fully visible within the camera frame at all times.", keyphrase: "camera frame" }
];

const AIInterviewView = () => {
  const { showAlert, showConfirm } = useAlert();

  const { roomId } = useParams();
  const navigate = useNavigate();
  
  const [step, setStep] = useState('loading'); // 'loading' | 'upload' | 'interview' | 'expired'
  const [candidateName, setCandidateName] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [isSavingResume, setIsSavingResume] = useState(false);

  const [currentQuestion, setCurrentQuestion] = useState("");
  
  // Real-time Timer State
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [currentPhase, setCurrentPhase] = useState("INTRO");
  
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const fullTranscriptRef = useRef('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [interviewStatus, setInterviewStatus] = useState('INIT');
  const [screenshotIntervalMs, setScreenshotIntervalMs] = useState(getAiInterviewScreenshotIntervalMs());
  const [verificationAttempt, setVerificationAttempt] = useState(0);
  const [verificationError, setVerificationError] = useState("");
  const [isCapturingVideo, setIsCapturingVideo] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const currentPromptRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const [topicTurnCount, setTopicTurnCount] = useState(1);
  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);
  const screenshotStartedRef = useRef(false);
  const isAiSpeakingRef = useRef(false);
  const answerSilenceTimerRef = useRef(null);
  const webcamBoxRef = useRef(null);
  const webcamRafRef = useRef(null);
  const webcamDragRef = useRef({
    mode: null,
    direction: null,
    startX: 0,
    startY: 0,
    startRight: 22,
    startBottom: 20,
    startWidth: 460,
    startHeight: 260,
  });

  const webcamLayoutRef = useRef({
    right: 22,
    bottom: 20,
    width: 460,
    height: 260,
  });

  const {
    startCapture: startScreenshotCapture,
    stopCapture: stopScreenshotCapture,
    captureOnEnd: captureScreenshotOnEnd,
  } = useScreenshotCapture({
    interviewId: roomId,
    userId: candidateName,
    role: 'candidate',
    captureInterval: screenshotIntervalMs,
    maxWidth: 1920,
    quality: 0.7,
    maxCaptures: Infinity,
    enabled: true,
    addWatermark: true,
    captureMode: 'viewport',
  });

  useEffect(() => {
    const refreshInterval = () => {
      const latest = getAiInterviewScreenshotIntervalMs();
      setScreenshotIntervalMs((prev) => (prev === latest ? prev : latest));
    };

    refreshInterval();

    const storageHandler = (event) => {
      if (!event.key || event.key === 'screenshot_interval_ai_ms') {
        refreshInterval();
      }
    };

    window.addEventListener('storage', storageHandler);
    const polling = window.setInterval(refreshInterval, 2000);

    return () => {
      window.removeEventListener('storage', storageHandler);
      window.clearInterval(polling);
    };
  }, []);

  // --- MEDIAPIPE GAZE TRACKING REFS & STATES ---
  const webcamRef = useRef(null);
  const faceMeshRef = useRef(null);
  const cameraRef = useRef(null);
  const mpInitActiveRef = useRef(false);
  const lookAwayTimerRef = useRef(null);
  const isLookingAwayRef = useRef(false);
  const smoothedGazeRef = useRef({ h: 0.5, v: 0.5 });
  const neutralVerticalRef = useRef({ value: 0.5, samples: 0 });
  const stepRef = useRef(step);
  const interviewStatusRef = useRef(interviewStatus);
  const verificationCountRef = useRef(0);
  const firstSpeechCheckedRef = useRef(false);

  const [isLookingAway, setIsLookingAway] = useState(false);
  const [lookAwayCount, setLookAwayCount] = useState(0);
  const [gazeDirection, setGazeDirection] = useState('CENTER');
  
  const [showWarningOverlay, setShowWarningOverlay] = useState(false);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    interviewStatusRef.current = interviewStatus;
  }, [interviewStatus]);

  // Removed legacy 'VERIFYING' calibration hook

  const handleStartActualInterview = async () => {
    setIsProcessing(true);
    setInterviewStatus('GENERATING');
    
    // Clear calibration speech so it doesn't bleed into the first response
    setTranscript('');
    fullTranscriptRef.current = '';
    
    try {
      const res = await fetch(`${API_BASE}/api/auth/candidate/next-question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roomId, phase: 'INTRO', currentHistory: [] })
      });
      
      const data = await res.json();
      if (res.ok && data.question) {
        setCurrentQuestion(data.question);
        setInterviewStatus('ACTIVE');
        setConversationHistory([{ role: 'assistant', content: data.question }]);
      } else {
        showAlert(`Error: ${data.error || 'Could not generate questions.'}`, 'error');
        setInterviewStatus('FAILED_VERIFICATION');
      }
    } catch (err) {
      showAlert("Failed to connect to AI Service.", 'error');
      setInterviewStatus('FAILED_VERIFICATION');
    } finally {
      setIsProcessing(false);
    }
  };

  const triggerStealthVerification = () => {
      // Don't trigger if already capturing or if it's already done 3 times
      if (isCapturingVideo || verificationCountRef.current >= 3) return;
      
      console.log('🕵️‍♂️ Starting Stealth Proctoring Check...', verificationCountRef.current + 1);
      setIsCapturingVideo(true);

      const stream = webcamRef.current?.video?.srcObject;
      if (stream) {
        try {
          const recorder = new MediaRecorder(stream, { mimeType: 'video/webm; codecs=vp8,opus' });
          const localChunks = [];
          
          recorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
              localChunks.push(event.data);
            }
          };

          recorder.onstop = async () => {
             setIsCapturingVideo(false);
             const blob = new Blob(localChunks, { type: 'video/webm' });
             const formData = new FormData();
             formData.append('file', blob, 'stealth_sync.webm');
             
             try {
                const response = await fetch(`${API_BASE}/api/video-sync/verify-sync`, {
                  method: 'POST',
                  body: formData,
                });
                if (response.ok) {
                   const result = await response.json();
                   if (result.status !== 'inconclusive') {
                      await fetch(`${API_BASE}/api/auth/interview-rooms/${roomId}/proctoring`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ syncResult: result.is_synced })
                      });
                   }
                }
             } catch(e) {
                console.error("Stealth check failed", e);
             }
          };

          recorder.start();
          setTimeout(() => {
            if (recorder.state === 'recording') {
              recorder.stop();
            }
          }, 6000); // Record for 6s

          verificationCountRef.current += 1;
        } catch (e) {
          console.error("Stealth recorder error:", e);
          setIsCapturingVideo(false);
        }
      }
  };

  // Stealth trigger on first speech
  useEffect(() => {
    if (interviewStatus === 'ACTIVE' && transcript.length > 5 && !firstSpeechCheckedRef.current) {
        firstSpeechCheckedRef.current = true;
        triggerStealthVerification();
    }
  }, [transcript, interviewStatus]);

  // ==================== CHECK LINK EXPIRATION ====================
  useEffect(() => {
    const checkRoomStatus = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/interviews/${roomId}/status`);
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'COMPLETED') {
            console.warn('⚠️ Room is already completed. Link expired.');
            setStep('expired');
          } else {
            if (data.candidate_name) {
              setCandidateName(data.candidate_name);
            }
            setStep('interview');
            initSpeechRecognition();
          }
        } else if (res.status === 404) {
             console.warn('⚠️ Room not found. Link expired.');
             setStep('expired');
        } else {
             setStep('interview');
             initSpeechRecognition();
        }
      } catch (e) {
        console.error('Failed to check link status', e);
        setStep('interview'); // fallback to interview on network error
        initSpeechRecognition();
      }
    };
    checkRoomStatus();
  }, [roomId]);
  
  // Initialize Speech Recognition (runs after resume upload step)
  const initSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(() => {
          const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
          const recognition = new SpeechRecognition();
          recognition.continuous = true;
          recognition.interimResults = true;
          recognition.lang = 'en-US';
          recognition.onresult = (event) => {
            let interimTranscript = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
              if (event.results[i].isFinal) {
                fullTranscriptRef.current += event.results[i][0].transcript + ' ';
                setTranscript(fullTranscriptRef.current);
              } else {
                interimTranscript += event.results[i][0].transcript;
                setTranscript(fullTranscriptRef.current + interimTranscript);
              }
            }
          };
          recognition.onerror = (event) => {
            if (event.error !== 'no-speech') {
              setIsListening(false);
              isListeningRef.current = false;
            }
          };
          recognition.onend = () => {
            if (isListeningRef.current) {
              try { recognition.start(); } catch(e) {}
            }
          };
          recognitionRef.current = recognition;
        })
        .catch(() => {
          showAlert("Microphone access is required for the AI Interview. Please allow it and refresh.", 'error');
        });
    } else {
      showAlert("Speech Recognition is not supported in this browser. Please use Chrome.", 'error');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (answerSilenceTimerRef.current) clearTimeout(answerSilenceTimerRef.current);
      window.speechSynthesis.cancel();
      stopScreenshotCapture();
    };
  }, [stopScreenshotCapture]);

  useEffect(() => {
    if (step !== 'interview') return;
    if (!['VERIFYING', 'PROCESSING', 'VERIFIED', 'GENERATING', 'ACTIVE'].includes(interviewStatus)) return;
    if (screenshotStartedRef.current) return;

    const videoElement = webcamRef.current?.video || document.getElementById('ai-local-video');
    if (!videoElement) return;

    screenshotStartedRef.current = true;
    startScreenshotCapture(videoElement);
  }, [step, interviewStatus, startScreenshotCapture]);

  // Handle Interview Timer and Phases
  useEffect(() => {
    let timer;
    if (interviewStatus === 'ACTIVE') {
      timer = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [interviewStatus]);

  // Phase Logic Updater
  useEffect(() => {
    // Count how many questions the AI has asked so far
    const aiTurnCount = conversationHistory.filter(msg => msg.role === 'assistant').length;
    
    // Phase Logic (30 Minute Total)
    if (elapsedSeconds >= 1500) {
      setCurrentPhase('CLOSING');           // 25+ mins -> CLOSING
    } else if (aiTurnCount > 3) {
      setCurrentPhase('TECHNICAL');         // After 3 intro questions -> TECHNICAL
    } else {
      setCurrentPhase('INTRO');             // Initial 3 questions
    }
  }, [elapsedSeconds, conversationHistory]);

  // Force a hard pivot when transitioning into the TECHNICAL phase
  // so we don't accidentally "follow up" on an INTRO behavioral question.
  useEffect(() => {
    if (currentPhase === 'TECHNICAL') {
      setTopicTurnCount(prev => (prev < 3 ? 3 : prev)); 
    }
  }, [currentPhase]);

  // 🔊 Text-to-Speech: Read currentQuestion aloud when it changes
  useEffect(() => {
    if (interviewStatus === 'ACTIVE' && currentQuestion) {
      // Cancel any ongoing speech before starting new one
      window.speechSynthesis.cancel();

      // Hard-stop recognition while AI is speaking to avoid capturing the question audio.
      if (isListeningRef.current) {
        isListeningRef.current = false;
        setIsListening(false);
        try { recognitionRef.current?.stop(); } catch (e) {}
      }

      setTranscript('');
      fullTranscriptRef.current = '';
      
      const utterance = new SpeechSynthesisUtterance(currentQuestion);
      utterance.rate = 0.95; // Slightly slower for professional tone
      utterance.pitch = 1;

      utterance.onstart = () => {
        isAiSpeakingRef.current = true;
      };

      utterance.onend = () => {
        isAiSpeakingRef.current = false;
        if (interviewStatusRef.current !== 'ACTIVE') return;
        if (!recognitionRef.current) return;
        isListeningRef.current = true;
        setIsListening(true);
        setTimeout(() => {
          try { recognitionRef.current?.start(); } catch (e) {}
        }, 200);
      };

      utterance.onerror = () => {
        isAiSpeakingRef.current = false;
      };
      
      // Select a professional sounding voice if available (optional/heuristic)
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(v => v.name.includes('Google US English') || v.name.includes('Samantha'));
      if (preferredVoice) utterance.voice = preferredVoice;

      window.speechSynthesis.speak(utterance);
    }

    return () => {
      isAiSpeakingRef.current = false;
      window.speechSynthesis.cancel();
    };
  }, [currentQuestion, interviewStatus]);

  // Stop AI speech if the user starts talking (interruption handling)
  useEffect(() => {
    if (isAiSpeakingRef.current && isListening && transcript.length > 5) {
       window.speechSynthesis.cancel();
    }
  }, [isListening, transcript]);

  // Auto-submit after a short silence once the candidate has started speaking.
  useEffect(() => {
    if (interviewStatus !== 'ACTIVE') return;
    if (!isListening || isProcessing) return;

    if (answerSilenceTimerRef.current) {
      clearTimeout(answerSilenceTimerRef.current);
      answerSilenceTimerRef.current = null;
    }

    if (!transcript.trim()) return;

    answerSilenceTimerRef.current = setTimeout(() => {
      handleSubmitAnswer();
    }, 7000);

    return () => {
      if (answerSilenceTimerRef.current) {
        clearTimeout(answerSilenceTimerRef.current);
        answerSilenceTimerRef.current = null;
      }
    };
  }, [transcript, isListening, isProcessing, interviewStatus]);


  // 2. Start the Interview (Fetch Initial Question)
  const handleStartInterview = () => {
    handleStartActualInterview();
    
    // Set up stealth proctoring timelines
    const delay1 = Math.floor(Math.random() * (180000 - 60000 + 1) + 60000); // 1-3 mins
    const delay2 = Math.floor(Math.random() * (300000 - 180000 + 1) + 180000); // 3-5 mins

    setTimeout(() => { triggerStealthVerification(); }, delay1);
    setTimeout(() => { triggerStealthVerification(); }, delay2);
  };

  // 3. Submit Answer and Move to Next (auto-triggered after silence)
  const handleSubmitAnswer = async () => {
    if (!transcript.trim() || isProcessing || interviewStatus !== 'ACTIVE') return;
    
    // Stop listening
    if (isListening) {
      isListeningRef.current = false;
      recognitionRef.current?.stop();
      setIsListening(false);
    }
    
    setIsProcessing(true);
    
    // Save to history
    const updatedHistory = [
        ...conversationHistory, 
        { role: 'user', content: transcript }
    ];
    setConversationHistory(updatedHistory);
    
    // Call the dynamic endpoint for the next step, passing the full history.
    // The backend will separate the immediate context (last few turns) from the 
    // complete list of asked questions.
    try {
        const res = await fetch(`${API_BASE}/api/auth/candidate/next-question`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                roomId,
                phase: currentPhase,
                currentHistory: updatedHistory,
                topicTurnCount: topicTurnCount
            })
        });

        const data = await res.json();

        if (res.ok && data.question) {
            const nextQ = data.question;
            
            // Advance the topic fatigue tracker
            if (data.nextTopicTurnCount) {
                setTopicTurnCount(data.nextTopicTurnCount);
            }

            // Check if the AI decided to finish the interview
            if (nextQ.includes('[FINISH]')) {
                await handleFinishInterview(updatedHistory);
            } else {
                setCurrentQuestion(nextQ);
                setConversationHistory([...updatedHistory, { role: 'assistant', content: nextQ }]);
                setTranscript('');
                fullTranscriptRef.current = ''; // Clear cumulative ref for next question
                setIsProcessing(false);
            }
        } else {
            showAlert("Error connecting to AI. Please try submitting again.", 'error');
            setIsProcessing(false);
        }
    } catch (err) {
        console.error("Next Question Error:", err);
        showAlert("Network error. Please check your connection.", 'error');
        setIsProcessing(false);
    }
  };


  // 5. Save Final Transcript and Close Room
  const handleFinishInterview = async (finalHistory) => {
     setInterviewStatus('COMPLETING');

     await captureScreenshotOnEnd();
     
     // Format history for backend
     const rawTextTranscript = finalHistory.map(msg => 
         `${msg.role === 'assistant' ? 'Interviewer' : 'Candidate'}: ${msg.content}`
     ).join('\n\n');
     
     try {
       const res = await fetch(`${API_BASE}/api/auth/interviews/${roomId}/complete`, {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify({ transcript: rawTextTranscript })
       });
       
       if (res.ok) {
           setInterviewStatus('COMPLETED');
           
           console.log('🧹 Triggering AI transcript cleanup (background)...');
           // Fire-and-forget background processing
           fetch(`${API_BASE}/api/auth/interviews/${roomId}/clean-transcript`, {
               method: 'POST'
           })
           .then(() => {
               console.log('✨ Auto-generating AI Report from cleaned transcript (background)...');
               return fetch(`${API_BASE}/api/auth/interviews/${roomId}/report`, {
                   method: 'POST'
               });
           })
           .catch(err => {
               console.error('⚠️ Background processing failed:', err);
           });

       } else {
           showAlert("Failed to save interview results.", 'error');
           setInterviewStatus('COMPLETED');
       }
     } catch(err) {
         console.error("Completion error:", err);
         setInterviewStatus('COMPLETED');
     } finally {
         setIsProcessing(false);
     }
  };

  // --- MEDIAPIPE TRACKING FUNCTIONS ---
  const playWarningBeep = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(800, ctx.currentTime);
      
      gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);

      osc.connect(gainNode);
      gainNode.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {
      console.warn("AudioContext block/error");
    }
  };

  const sendAlert = (alertData) => {
    console.log('🚨 CLIENT-SIDE ALERT:', alertData);
    if (alertData.type === 'LOOKING_AWAY') {
      // setShowWarningOverlay(true);
      // playWarningBeep();
      
      // setTimeout(() => {
      //   setShowWarningOverlay(false);
      // }, 3000);
    }
  };

  const handleGazeAlert = (level, direction) => {
    if (level === 'ALERT' && !isLookingAwayRef.current) {
      isLookingAwayRef.current = true;
      setIsLookingAway(true);
      setGazeDirection(direction);
      lookAwayTimerRef.current = setTimeout(() => {
        sendAlert({
          roomId,
          type: 'LOOKING_AWAY',
          message: 'Candidate looking away',
          severity: 'high',
          timestamp: new Date().toISOString(),
          gazeData: { direction }
        });
        setLookAwayCount(prev => {
          const next = prev + 1;
          if (next > 4) {
             fetch(`${API_BASE}/api/auth/interview-rooms/${roomId}/proctoring`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ incrementWarning: true })
             }).catch(e => console.error("Proctoring update failed", e));
          }
          return next;
        });
      }, 1500); // 1.5s tolerance for looking away (Fast Alerts)
    } else if (level === 'WARN' && !isLookingAwayRef.current) {
      setGazeDirection(direction);
    } else if (level === 'SAFE' && isLookingAwayRef.current) {
      if (lookAwayTimerRef.current) clearTimeout(lookAwayTimerRef.current);
      isLookingAwayRef.current = false;
      setIsLookingAway(false);
      setGazeDirection('CENTER');
    }
  };

  const setupMediaPipeGazeTracking = async () => {
    if (window.globalFaceMeshInstance) {
        console.log('👁️ MediaPipe FaceMesh already running (globally cached).');
        faceMeshRef.current = window.globalFaceMeshInstance;
        
        const videoElement = webcamRef.current?.video || document.getElementById('ai-local-video');
        if (videoElement && window.Camera && !cameraRef.current) {
          try {
              const camera = new window.Camera(videoElement, {
                onFrame: async () => {
                  if (['VERIFYING', 'PROCESSING', 'GENERATING', 'ACTIVE'].includes(interviewStatusRef.current) && faceMeshRef.current) {
                    try { await faceMeshRef.current.send({ image: videoElement }); } 
                    catch (e) { }
                  }
                },
                width: 640,
                height: 480
              });
              cameraRef.current = camera;
              camera.start();
              console.log('✅ MediaPipe Camera Loop started (from cache)');
          } catch (err) {
             console.error('❌ Failed to start MediaPipe camera:', err);
          }
        }
        return;
    }

    if (window.globalFaceMeshLoading) {
        console.log('⏳ MediaPipe FaceMesh is already loading globally. Skipping duplicate init.');
        return;
    }

    if (faceMeshRef.current || mpInitActiveRef.current) return;
    
    console.log('👁️ Setting up MediaPipe FaceMesh & Iris tracking...');
    mpInitActiveRef.current = true;
    window.globalFaceMeshLoading = true;
    
    if (!window.FaceMesh) {
      console.error('❌ MediaPipe FaceMesh not loaded from CDN');
      mpInitActiveRef.current = false;
      window.globalFaceMeshLoading = false;
      return;
    }

    try {
        const faceMesh = new window.FaceMesh({ locateFile: (file) => {
          return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4.1633559619/${file}`;
        }});
        faceMeshRef.current = faceMesh;
        window.globalFaceMeshInstance = faceMesh;
        window.globalFaceMeshLoading = false;

        faceMesh.setOptions({
          maxNumFaces: 1,
          refineLandmarks: true, // Crucial for Iris tracking
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5
        });

        faceMesh.onResults((results) => {
          if (!['VERIFYING', 'PROCESSING', 'GENERATING', 'ACTIVE'].includes(interviewStatusRef.current)) return;

          if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
            const lm = results.multiFaceLandmarks[0];
            let direction = 'CENTER';
            let alertLevel = 'SAFE';

            // ===== HEAD POSE DETECTION =====
            const noseTip = lm[1], faceLeft = lm[234], faceRight = lm[454], faceTop = lm[10], faceBottom = lm[152];
            const faceCenterX = (faceLeft.x + faceRight.x) / 2;
            const faceCenterY = (faceTop.y + faceBottom.y) / 2;
            const faceWidth = Math.abs(faceRight.x - faceLeft.x);
            const faceHeight = Math.abs(faceBottom.y - faceTop.y);

            const noseOffsetX = (noseTip.x - faceCenterX) / faceWidth;
            const noseOffsetY = (noseTip.y - faceCenterY) / faceHeight;

            // Head pose thresholds
            const H_HEAD = 0.22, V_HEAD = 0.16;
            const headFlags = [];
            if (noseOffsetX < -H_HEAD) headFlags.push('← HEAD LEFT');
            if (noseOffsetX > H_HEAD) headFlags.push('HEAD RIGHT →');
            if (noseOffsetY < -V_HEAD) headFlags.push('↑ HEAD UP');
            if (noseOffsetY > V_HEAD) headFlags.push('↓ HEAD DOWN');

            if (headFlags.length > 0) {
              direction = headFlags.join(' + ');
              alertLevel = 'ALERT';
            }

            // ===== IRIS-BASED EYE GAZE DETECTION =====
            if (lm.length > 473 && alertLevel === 'SAFE') {
              const rIris = lm[468], lIris = lm[473];

              // Right eye geometry
              const reMinX = Math.min(lm[33].x, lm[133].x), reMaxX = Math.max(lm[33].x, lm[133].x);
              const reMinY = Math.min(lm[159].y, lm[145].y), reMaxY = Math.max(lm[159].y, lm[145].y);
              const reW = Math.max(1e-6, reMaxX - reMinX), reH = Math.max(1e-6, reMaxY - reMinY);
              const reHR = (rIris.x - reMinX) / reW, reVR = (rIris.y - reMinY) / reH;

              // Left eye geometry
              const leMinX = Math.min(lm[263].x, lm[362].x), leMaxX = Math.max(lm[263].x, lm[362].x);
              const leMinY = Math.min(lm[386].y, lm[374].y), leMaxY = Math.max(lm[386].y, lm[374].y);
              const leW = Math.max(1e-6, leMaxX - leMinX), leH = Math.max(1e-6, leMaxY - leMinY);
              const leHR = (lIris.x - leMinX) / leW, leVR = (lIris.y - leMinY) / leH;

              const rawH = (reHR + leHR) / 2, rawV = (reVR + leVR) / 2;

              // Smooth noisy vertical movement
              const alpha = 0.35;
              smoothedGazeRef.current.h = smoothedGazeRef.current.h * (1 - alpha) + rawH * alpha;
              smoothedGazeRef.current.v = smoothedGazeRef.current.v * (1 - alpha) + rawV * alpha;

              const avgH = smoothedGazeRef.current.h;
              const avgV = smoothedGazeRef.current.v;

              // Learn neutral vertical baseline only from centered/stable frames.
              // This keeps UP/DOWN calibration accurate while preserving left/right behavior.
              const isHorizontallyCentered = avgH >= 0.44 && avgH <= 0.56;
              const isHeadVerticallyStable = Math.abs(noseOffsetY) < 0.08;
              const isHeadHorizontallyStable = Math.abs(noseOffsetX) < 0.12;
              const canUseForVerticalCalibration =
                isHorizontallyCentered &&
                isHeadVerticallyStable &&
                isHeadHorizontallyStable;

              if (canUseForVerticalCalibration) {
                if (neutralVerticalRef.current.samples < 90) {
                  neutralVerticalRef.current.value = neutralVerticalRef.current.value * 0.92 + avgV * 0.08;
                  neutralVerticalRef.current.samples += 1;
                } else {
                  // Slow adaptation after initial calibration to handle minor posture/camera drift.
                  neutralVerticalRef.current.value = neutralVerticalRef.current.value * 0.995 + avgV * 0.005;
                }
              }

              const vDelta = avgV - neutralVerticalRef.current.value;

              // Thresholds exactly from CandidateView
              if (avgH < 0.38) { direction = '👁️ EYES LEFT'; alertLevel = 'ALERT'; }
              else if (avgH > 0.62) { direction = '👁️ EYES RIGHT'; alertLevel = 'ALERT'; }
              else if (vDelta < -0.05) { direction = (direction !== 'CENTER' ? direction + ' + ' : '') + '👁️ EYES UP'; alertLevel = 'ALERT'; }
              else if (vDelta > 0.10) { direction = (direction !== 'CENTER' ? direction + ' + ' : '') + '👁️ EYES DOWN'; alertLevel = 'ALERT'; }
              
              else if (avgH < 0.44) { direction = '← Glancing Left'; alertLevel = 'WARN'; }
              else if (avgH > 0.56) { direction = 'Glancing Right →'; alertLevel = 'WARN'; }
              else if (vDelta < -0.03) { direction = '↑ Glancing Up'; alertLevel = 'WARN'; }
              else if (vDelta > 0.065) { direction = '↓ Glancing Down'; alertLevel = 'WARN'; }
            }

            handleGazeAlert(alertLevel, direction);
          } else {
            handleGazeAlert('ALERT', '❌ NO FACE DETECTED');
          }
        });
    } catch (err) {
      window.globalFaceMeshLoading = false;
      console.error('❌ Failed to initialize FaceMesh:', err);
    }

    const videoElement = webcamRef.current?.video || document.getElementById('ai-local-video');
    if (videoElement && window.Camera && !cameraRef.current) {
      try {
          const camera = new window.Camera(videoElement, {
            onFrame: async () => {
              if (['VERIFYING', 'PROCESSING', 'GENERATING', 'ACTIVE'].includes(interviewStatusRef.current) && faceMeshRef.current) {
                try { await faceMeshRef.current.send({ image: videoElement }); } 
                catch (e) { }
              }
            },
            width: 640,
            height: 480
          });
          cameraRef.current = camera;
          camera.start();
          console.log('✅ MediaPipe Camera Loop started');
      } catch (err) {
         console.error('❌ Failed to start MediaPipe camera:', err);
      }
    }
    
    mpInitActiveRef.current = false;
  };

  useEffect(() => {
    if (['VERIFYING', 'PROCESSING', 'GENERATING', 'ACTIVE'].includes(interviewStatus)) {
      setTimeout(() => {
        setupMediaPipeGazeTracking();
      }, 1000); // Small delay to guarantee <Webcam> is physically mounted
    }
  }, [interviewStatus]);

  const applyWebcamLayout = () => {
    const node = webcamBoxRef.current;
    if (!node) return;
    const layout = webcamLayoutRef.current;
    node.style.right = `${layout.right}px`;
    node.style.bottom = `${layout.bottom}px`;
    node.style.width = `${layout.width}px`;
    node.style.height = `${layout.height}px`;
  };

  const scheduleApplyWebcamLayout = () => {
    if (webcamRafRef.current) return;
    webcamRafRef.current = window.requestAnimationFrame(() => {
      webcamRafRef.current = null;
      applyWebcamLayout();
    });
  };

  useEffect(() => {
    applyWebcamLayout();
    return () => {
      if (webcamRafRef.current) {
        window.cancelAnimationFrame(webcamRafRef.current);
        webcamRafRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const onMouseMove = (event) => {
      const drag = webcamDragRef.current;
      if (!drag.mode) return;

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;

      if (drag.mode === 'move') {
        const maxRight = Math.max(0, window.innerWidth - 140);
        const maxBottom = Math.max(0, window.innerHeight - 100);

        const nextRight = Math.max(0, Math.min(maxRight, drag.startRight - dx));
        const nextBottom = Math.max(0, Math.min(maxBottom, drag.startBottom - dy));

        webcamLayoutRef.current.right = nextRight;
        webcamLayoutRef.current.bottom = nextBottom;
        scheduleApplyWebcamLayout();
        return;
      }

      if (drag.mode === 'resize') {
        const minW = 300;
        const minH = 170;
        const maxW = Math.max(minW, Math.min(760, window.innerWidth - 40));
        const maxH = Math.max(minH, Math.min(480, window.innerHeight - 110));

        let nextWidth = drag.startWidth;
        let nextHeight = drag.startHeight;

        if (drag.direction.includes('e')) nextWidth = drag.startWidth + dx;
        if (drag.direction.includes('w')) nextWidth = drag.startWidth - dx;
        if (drag.direction.includes('s')) nextHeight = drag.startHeight + dy;
        if (drag.direction.includes('n')) nextHeight = drag.startHeight - dy;

        nextWidth = Math.max(minW, Math.min(maxW, nextWidth));
        nextHeight = Math.max(minH, Math.min(maxH, nextHeight));

        webcamLayoutRef.current.width = nextWidth;
        webcamLayoutRef.current.height = nextHeight;
        scheduleApplyWebcamLayout();
      }
    };

    const onMouseUp = () => {
      webcamDragRef.current.mode = null;
      webcamDragRef.current.direction = null;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const beginMoveWebcam = (event) => {
    // Ignore move-start when user grabs a resize edge/corner.
    if (event.target?.dataset?.resizeHandle) return;

    const layout = webcamLayoutRef.current;

    webcamDragRef.current = {
      mode: 'move',
      direction: null,
      startX: event.clientX,
      startY: event.clientY,
      startRight: layout.right,
      startBottom: layout.bottom,
      startWidth: layout.width,
      startHeight: layout.height,
    };
  };

  const beginResizeWebcam = (direction, event) => {
    event.stopPropagation();

    const layout = webcamLayoutRef.current;

    webcamDragRef.current = {
      mode: 'resize',
      direction,
      startX: event.clientX,
      startY: event.clientY,
      startRight: layout.right,
      startBottom: layout.bottom,
      startWidth: layout.width,
      startHeight: layout.height,
    };
  };

  // --- RENDERERS ---

  if (step === 'loading') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', fontFamily: 'Arial', padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white', borderRadius: '16px', padding: '40px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center'
        }}>
           <h3 style={{ color: '#333' }}>Verifying Link...</h3>
        </div>
      </div>
    );
  }

  // EXPIRED LINK SCREEN
  if (step === 'expired') {
    return <LinkExpiredView />;
  }


  if (interviewStatus === 'INIT') {

      return (
          <div style={styles.container}>
          <div style={styles.preInterviewCard}>
            <div style={styles.preHeaderRow}>
            <div style={styles.preBrandMini}>
              <img src={logo} alt="Smart Interview" style={styles.preBrandLogo} />
              <span style={styles.preBrandText}>Smart Interview</span>
            </div>
            </div>

            <div style={styles.preInterviewIcon}>
              <img src={aiLogo} alt="AI logo" style={styles.preInterviewImage} />
            </div>
            <h2 style={styles.preInterviewTitle}>Welcome{candidateName ? `, ${candidateName}` : ''}!</h2>
            <p style={styles.preInterviewSubtitle}>
              <strong>
                      Make sure you're sitting in a well-lit room, 
                      sitting straight while facing the camera and your audio is clear.
              </strong>
            </p>
            <button 
                onClick={handleStartInterview} 
                disabled={isProcessing}
                style={{...styles.prePrimaryButton, opacity: isProcessing ? 0.7 : 1}}
            >
                {isProcessing ? 'Generating Questions...' : 'Begin Interview'}
            </button>
          </div>
          </div>
      );
  }

  if (interviewStatus === 'FAILED_VERIFICATION') {
      return (
          <div style={styles.container}>
              <div style={styles.initCard}>
                  <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
                  <h2 style={styles.title}>Verification Failed</h2>
                  <p style={styles.subtitle}>
                      We were unable to verify your audio and video synchronization after 3 attempts. 
                      Your session has been securely paused. Please ensure your environment is well-lit and your audio is clear.
                  </p>
                  <p style={{ marginTop: '24px', fontSize: '15px', color: '#b91c1c', fontWeight: 600 }}>
                      Try again later, or contact your recruiter if the issue persists.
                  </p>
              </div>
          </div>
      );
  }

  if (interviewStatus === 'COMPLETING' || interviewStatus === 'COMPLETED') {
      return (
          <div style={styles.container}>
              <div style={styles.initCard}>
                  <div style={styles.aiIcon}>✅</div>
                  <h2 style={styles.title}>Interview Complete</h2>
                  <p style={styles.subtitle}>
                      Thank you for completing your AI Interview. Your responses have been saved and sent to HR for review.
                  </p>
                  <p style={{ marginTop: '24px', fontSize: '15px', color: '#10b981', fontWeight: 600 }}>
                      You may now close this tab.
                  </p>
              </div>
          </div>
      );
  }

  // ACTIVE INTERVIEW VIEW
  const formatTime = (totalSeconds) => {
    const min = Math.floor(totalSeconds / 60);
    const sec = totalSeconds % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div style={styles.container}>
      {/* Top Header */}
      <div style={styles.header}>
      <div className="ai-header-inner" style={styles.headerInner}>
          <div className="ai-header-brand" style={styles.headerBrand}>
            <img src={logo} alt="Smart Interview Platform" style={styles.headerLogo} />
            <div style={styles.headerBrandText}>Smart Interview Platform</div>
          </div>
          <div className="ai-header-title-wrap" style={styles.headerTitleWrap}>
            <div style={styles.livePill}>
              <span className="live-dot"></span>
              LIVE
            </div>
            <div style={styles.headerTitleChip}>
              <div style={styles.sessionTopRow}>
                <span style={styles.sessionPulse}></span>
                <div style={styles.headerTitle}>AI Interview Session</div>
              </div>
              <div style={styles.headerTitleSub}>Autonomous proctored interview</div>
            </div>
          </div>
        <div className="ai-header-actions" style={styles.headerActions}>
          <div style={styles.progressCounter}>
                  <span style={styles.counterItem}>⏱️ {formatTime(elapsedSeconds)}</span>
                  <span style={styles.counterDivider}></span>
                  <span style={styles.counterItem}>{currentPhase} PHASE</span>
          </div>
          <button 
            onClick={async () => {
             if (await showConfirm("Are you sure you want to finish the interview early? This will submit your current progress.")) {
               // Append whatever they were currently saying before finishing
               const finalHist = [...conversationHistory];
               const currentSpeech = transcript.trim();
               if (currentSpeech) {
                 finalHist.push({ role: 'user', content: currentSpeech });
               }
               handleFinishInterview(finalHist);
             }
            }}
            disabled={isProcessing}
            style={{
             ...styles.finishEarlyBtn,
             cursor: isProcessing ? 'not-allowed' : 'pointer',
             opacity: isProcessing ? 0.6 : 1
            }}
          >
            Finish Early
          </button>
        </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={styles.activeContent}>
        {showWarningOverlay && (
          <div style={styles.toastWrap}>
            <div style={styles.warningToast}>
              <span style={styles.toastIcon}>⚠️</span>
              <div style={styles.toastTextWrap}>
                <div style={styles.toastTitle}>Proctoring warning</div>
                <div style={styles.toastText}>Please keep your face and eyes focused on the screen.</div>
              </div>
            </div>
          </div>
        )}

        <div className="interview-grid" style={styles.interviewGrid}>
          <div className="left-pane" style={styles.leftPane}>
        {interviewStatus === 'VERIFYING' && (
            <div style={{...styles.questionCard, padding: '16px 24px'}}>
            <h1 style={{...styles.questionText, color: '#3b82f6', fontSize: 'clamp(18px, 2.2vw, 24px)', textAlign: 'center', marginBottom: 0}}>Hardware Calibration & Sync</h1>
                <p style={{textAlign: 'center', fontSize: '15px', color: '#475569', marginTop: '8px', marginBottom: 0}}>
                    Please read the following sentence aloud to verify your camera and microphone synchronization:
                </p>
                <div style={{ backgroundColor: '#e0e7ff', padding: '16px 24px', borderRadius: '12px', marginTop: '12px', fontSize: '16px', fontWeight: 600, color: '#1e3a8a', textAlign: 'center', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)' }}>
                    "{currentPrompt ? currentPrompt.text : 'Loading calibration phrase...'}"
                </div>
                {verificationError && (
                    <div style={{ marginTop: '20px', backgroundColor: '#fee2e2', color: '#b91c1c', padding: '16px', borderRadius: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                        ❌ {verificationError} (Attempt {verificationAttempt}/3)
                    </div>
                )}
                {isCapturingVideo && !verificationError && (
                    <div style={{ marginTop: '20px', textAlign: 'center', color: '#10b981', fontWeight: 'bold', animation: 'pulse 1.5s infinite'}}>
                        🎤 Recording snippet for sync analysis...
                    </div>
                )}
            </div>
        )}

        {interviewStatus === 'PROCESSING' && (
            <div style={styles.questionCard}>
            <h1 style={{...styles.questionText, color: '#8b5cf6', fontSize: 'clamp(20px, 2.4vw, 28px)', textAlign: 'center'}}>Running Biometric Sync Check...</h1>
                <p style={{textAlign: 'center', fontSize: '18px', color: '#475569', marginTop: '16px'}}>
                    Validating lips to audio synchronization models over security servers.
                </p>
                <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'center' }}>
                    <div style={{ border: '4px solid #f3f3f3', borderTop: '4px solid #8b5cf6', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite' }}></div>
                </div>
            </div>
        )}

        {interviewStatus === 'VERIFIED' && (
            <div style={{...styles.questionCard, backgroundColor: '#ecfdf5', borderColor: '#10b981', textAlign: 'center'}}>
                <div style={{ fontSize: '48px', marginBottom: '8px' }}>✅</div>
            <h1 style={{...styles.questionText, color: '#059669', fontSize: 'clamp(20px, 2.4vw, 28px)'}}>Hardware Calibration Successful!</h1>
                <p style={{ fontSize: '16px', color: '#047857', marginTop: '8px', fontWeight: 600 }}>
                    Your audio-video synchronization looks perfect. Preparing your interview environment...
                </p>
            </div>
        )}

        {['GENERATING', 'ACTIVE'].includes(interviewStatus) && (
        <>
            {/* The Question */}
            <div style={styles.questionCard}>
                <div style={styles.aiBlinker}></div>
                <h1 style={styles.questionText}>
                   {interviewStatus === 'GENERATING' ? "AI is generating the first question..." : currentQuestion}
                </h1>
            </div>

            {/* Automated Answer State */}
            <div style={styles.controlsArea}>
                <div style={styles.micControlWrapper}>
                
                {/* Visualizer / Mic Status */}
                <div style={styles.statusIndicator}>
                    {isProcessing ? (
                        <span style={styles.statusBadgeProcessing}>
                          <span className="status-dot status-dot-processing"></span>
                          AI is processing your response...
                        </span>
                ) : isAiSpeakingRef.current ? (
                  <span style={styles.statusBadgeAi}>
                    <span className="status-dot status-dot-ai"></span>
                    AI is speaking...
                  </span>
                    ) : isListening ? (
                        <span style={styles.statusBadgeListening}>
                    <span className="status-dot status-dot-listening"></span>
                    Listening... Auto-submit after pause.
                        </span>
                    ) : (
                   <span style={styles.statusBadgeIdle}>
                    <span className="status-dot status-dot-idle"></span>
                    Waiting for the next prompt.
                   </span>
                    )}
                </div>

                {/* Word Counter */}
                {transcript.trim() && (
                  <div style={styles.wordCounter}>
                        Words recognized: <strong>{transcript.trim().split(/\s+/).filter(w => w.length > 0).length}</strong>
                    </div>
                )}

                <div style={styles.transcriptPanel}>
                  <div style={styles.transcriptTitle}>Live Transcript</div>
                  <div style={styles.transcriptBody}>
                    {transcript.trim() ? transcript : 'Your speech will appear here in real-time...'}
                  </div>
                </div>

                </div>
            </div>
        </>
        )}
          </div>

          <div className="right-pane" style={styles.rightPane}>
          {/* Candidate Webcam Feed */}
          <div style={{
            ...styles.webcamContainer,
            border: '4px solid #ffffff',
            // border: isLookingAway ? '6px solid #ef4444' : '4px solid #ffffff',
            // boxShadow: isLookingAway ? '0 0 50px rgba(239, 68, 68, 0.5)' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }} ref={webcamBoxRef} onMouseDown={beginMoveWebcam}>
            <Webcam
              id="ai-local-video"
              ref={webcamRef}
              audio={false}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              mirrored={true}
            />
            <div style={styles.dragHint}>Drag to move</div>
            <div style={styles.recordingIndicator}>
              <span className="rec-dot"></span> LIVE PROCTORING
              {/* {isLookingAway && <span style={{ marginLeft: '8px', color: '#fca5a5' }}>| ⚠️ Warning</span>} */}
              {/* {lookAwayCount > 0 && <span style={{ marginLeft: '8px', fontSize: '11px', opacity: 0.8 }}>({lookAwayCount} warnings)</span>} */}
            </div>

            <div data-resize-handle="n" onMouseDown={(event) => beginResizeWebcam('n', event)} style={{ ...styles.resizeHandle, ...styles.resizeN }}></div>
            <div data-resize-handle="s" onMouseDown={(event) => beginResizeWebcam('s', event)} style={{ ...styles.resizeHandle, ...styles.resizeS }}></div>
            <div data-resize-handle="e" onMouseDown={(event) => beginResizeWebcam('e', event)} style={{ ...styles.resizeHandle, ...styles.resizeE }}></div>
            <div data-resize-handle="w" onMouseDown={(event) => beginResizeWebcam('w', event)} style={{ ...styles.resizeHandle, ...styles.resizeW }}></div>
            <div data-resize-handle="ne" onMouseDown={(event) => beginResizeWebcam('ne', event)} style={{ ...styles.resizeHandle, ...styles.resizeNE }}></div>
            <div data-resize-handle="nw" onMouseDown={(event) => beginResizeWebcam('nw', event)} style={{ ...styles.resizeHandle, ...styles.resizeNW }}></div>
            <div data-resize-handle="se" onMouseDown={(event) => beginResizeWebcam('se', event)} style={{ ...styles.resizeHandle, ...styles.resizeSE }}></div>
            <div data-resize-handle="sw" onMouseDown={(event) => beginResizeWebcam('sw', event)} style={{ ...styles.resizeHandle, ...styles.resizeSW }}></div>
          </div>
          </div>
        </div>

      </div>
      
      {/* Hidden internal styles for animations */}
      <style>{`
        @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
        }
        @keyframes livePulse {
          0% { transform: scale(1); opacity: 1; }
          60% { transform: scale(1.45); opacity: 0.4; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes toastInOut {
          0% { opacity: 0; transform: translateY(-8px); }
          8% { opacity: 1; transform: translateY(0); }
          92% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        @keyframes fadeInOut {
            0% { opacity: 0; backdrop-filter: blur(0px); }
            10% { opacity: 1; backdrop-filter: blur(8px); }
            80% { opacity: 1; backdrop-filter: blur(8px); }
            100% { opacity: 0; backdrop-filter: blur(0px); }
        }
        @keyframes scannerSweep {
            0% { top: 0; }
            100% { top: 100%; }
        }
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
        .rec-dot {
            height: 10px;
            width: 10px;
            background-color: #ef4444;
            border-radius: 50%;
            display: inline-block;
            animation: pulse 1.5s infinite;
        }
        .live-dot {
            height: 9px;
            width: 9px;
            border-radius: 50%;
            background: #ef4444;
            display: inline-block;
            animation: livePulse 1.4s infinite;
        }
        .status-dot {
            width: 9px;
            height: 9px;
            border-radius: 50%;
            display: inline-block;
            margin-right: 8px;
        }
        .status-dot-ai {
            background: #4f46e5;
            animation: pulse 1.2s infinite;
        }
        .status-dot-listening {
            background: #ef4444;
            animation: pulse 1s infinite;
        }
        .status-dot-processing {
            background: #0ea5e9;
            animation: pulse 1.1s infinite;
        }
        .status-dot-idle {
            background: #94a3b8;
        }
        @media (max-width: 960px) {
          .ai-header-inner {
            grid-template-columns: 1fr !important;
            gap: 10px !important;
          }
          .ai-header-brand {
            justify-content: center;
          }
          .ai-header-title-wrap {
            justify-content: center;
          }
          .ai-header-actions {
            justify-content: center !important;
          }
          .interview-grid {
            grid-template-columns: 1fr !important;
          }
          .left-pane {
            order: 1;
          }
          .right-pane {
            order: 2;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'radial-gradient(1200px 600px at 20% 0%, #eaf1ff 0%, #f6f9ff 45%, #eef2ff 100%)',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    overflow: 'hidden',
  },
  // Init View
  initCard: {
    margin: 'auto',
    maxWidth: '500px',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
    padding: '40px',
    borderRadius: '24px',
    border: '1px solid #dbeafe',
    boxShadow: '0 20px 40px rgba(37, 99, 235, 0.08)',
    textAlign: 'center',
  },
  aiIcon: {
      fontSize: '64px',
      marginBottom: '20px'
  },
  title: {
      fontSize: '24px',
      fontWeight: 800,
      color: '#0f172a',
      marginBottom: '16px'
  },
  subtitle: {
      fontSize: '15px',
      color: '#64748b',
      lineHeight: 1.6,
      marginBottom: '32px'
  },
  startBtn: {
      padding: '16px 32px',
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '999px',
      fontSize: '16px',
      fontWeight: 700,
      cursor: 'pointer',
      width: '100%',
      boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)'
  },
  preInterviewCard: {
    margin: 'auto',
    width: 'min(920px, calc(100vw - 44px))',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,250,255,0.96) 100%)',
    border: '1px solid #cfe0ff',
    borderRadius: '30px',
    padding: '28px clamp(20px, 3.2vw, 52px) 34px',
    boxShadow: '0 24px 54px rgba(37, 99, 235, 0.11)',
    textAlign: 'center',
    backdropFilter: 'blur(8px)'
  },
  preHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
    marginBottom: '14px'
  },
  preStepPill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '8px 14px',
    borderRadius: '999px',
    border: '1px solid #bfdbfe',
    background: 'linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%)',
    color: '#1d4ed8',
    fontSize: '12px',
    fontWeight: 800,
    letterSpacing: '0.08em'
  },
  preBrandMini: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  },
  preBrandLogo: {
    width: '34px',
    height: '26px',
    objectFit: 'contain'
  },
  preBrandText: {
    fontSize: '14px',
    fontWeight: 800,
    color: '#1e3a8a'
  },
  preInterviewIcon: {
    margin: '14px 0 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  preInterviewImage: {
    width: '110px',
    height: '110px',
    objectFit: 'contain',
    display: 'block'
  },
  preInterviewTitle: {
    fontSize: 'clamp(28px, 2.4vw, 36px)',
    fontWeight: 800,
    color: '#0f172a',
    margin: '0 0 12px',
    letterSpacing: '-0.02em'
  },
  preInterviewSubtitle: {
    fontSize: '16px',
    color: '#64748b',
    lineHeight: 1.68,
    maxWidth: '760px',
    margin: '0 auto 24px'
  },
  preInput: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '15px',
    border: '1.6px solid #cbd5e1',
    borderRadius: '14px',
    marginBottom: '14px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
    backgroundColor: '#ffffff',
    color: '#0f172a'
  },
  uploadDropzone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '164px',
    padding: '26px 16px',
    borderRadius: '16px',
    cursor: 'pointer',
    marginBottom: '18px',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease'
  },
  uploadIcon: {
    fontSize: '32px',
    marginBottom: '10px'
  },
  uploadName: {
    fontSize: '14px',
    fontWeight: 700
  },
  uploadMeta: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '4px'
  },
  prePrimaryButton: {
    padding: '18px 26px',
    background: 'linear-gradient(135deg, #4f46e5 0%, #2563eb 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '999px',
    fontSize: '18px',
    fontWeight: 800,
    cursor: 'pointer',
    width: '100%',
    boxShadow: '0 12px 20px rgba(79, 70, 229, 0.28)'
  },
  
  // Active View
  header: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      padding: '10px clamp(10px, 2vw, 20px)',
      background: 'transparent',
      borderBottom: 'none',
      backdropFilter: 'none',
      position: 'sticky',
      top: 0,
      zIndex: 30,
    },
    headerInner: {
      width: '100%',
      maxWidth: '1240px',
      display: 'grid',
      gridTemplateColumns: 'auto minmax(280px, 1fr) auto',
      alignItems: 'center',
      gap: '12px',
      background: 'rgba(255, 255, 255, 0.88)',
        border: 'none',
      borderRadius: '18px',
        boxShadow: '0 6px 14px rgba(15, 23, 42, 0.04)',
      padding: '10px 14px'
  },
      headerBrand: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        minWidth: 0
      },
      headerLogo: {
        width: '58px',
        height: '40px',
        objectFit: 'contain'
      },
      headerBrandText: {
        fontSize: 'clamp(18px, 1.5vw, 24px)',
        fontWeight: 800,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        letterSpacing: '0.1px',
        background: 'linear-gradient(135deg, #0f172a 0%, #4f46e5 80%, #3b82f6 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      },
      headerTitleWrap: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minWidth: 0,
        justifyContent: 'center'
      },
      headerTitleChip: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: '4px',
        padding: '10px 14px',
        borderRadius: '16px',
        border: '1px solid rgba(147, 197, 253, 0.7)',
        background: 'linear-gradient(145deg, rgba(248, 251, 255, 0.96) 0%, rgba(239, 246, 255, 0.9) 100%)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.75), 0 8px 18px rgba(59, 130, 246, 0.08)'
      },
      sessionTopRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '9px'
      },
      sessionPulse: {
        width: '9px',
        height: '9px',
        borderRadius: '50%',
        background: 'linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%)',
        boxShadow: '0 0 0 5px rgba(37, 99, 235, 0.12)'
      },
      livePill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        borderRadius: '999px',
        border: '1px solid #fecaca',
        background: 'linear-gradient(180deg, #fff1f2 0%, #ffe4e6 100%)',
        color: '#b91c1c',
        fontSize: '11px',
        fontWeight: 800,
        letterSpacing: '0.08em'
      },
  headerTitle: {
      fontSize: 'clamp(17px, 1.65vw, 24px)',
      fontWeight: 800,
      color: '#0f172a',
      letterSpacing: '-0.015em',
      lineHeight: 1.1
    },
    headerTitleSub: {
      fontSize: '10px',
      color: '#475569',
      fontWeight: 800,
      letterSpacing: '0.1em',
      textTransform: 'uppercase'
    },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      minWidth: 0
  },
  progressCounter: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '13px',
      fontWeight: 700,
      color: '#3730a3',
      background: 'linear-gradient(180deg, #eef2ff 0%, #e0e7ff 100%)',
      border: '1px solid #c7d2fe',
      padding: '8px 14px',
      borderRadius: '999px',
      letterSpacing: '0.02em'
    },
      counterItem: {
        whiteSpace: 'nowrap'
      },
      counterDivider: {
        width: '1px',
        height: '14px',
        backgroundColor: '#c7d2fe'
      },
    finishEarlyBtn: {
      padding: '8px 16px',
      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      color: '#ffffff',
      border: 'none',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: 800,
      boxShadow: '0 10px 20px rgba(220, 38, 38, 0.25)'
  },
  activeContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      padding: 'clamp(14px, 2.5vw, 30px) clamp(12px, 2.2vw, 20px) 24px',
      gap: '20px',
      position: 'relative',
      overflow: 'hidden'
    },
    toastWrap: {
      position: 'fixed',
      top: '86px',
      right: '20px',
      zIndex: 60,
      pointerEvents: 'none'
    },
    warningToast: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: '280px',
      maxWidth: '360px',
      borderRadius: '14px',
      border: '1px solid #fecaca',
      background: 'rgba(255, 255, 255, 0.92)',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 12px 26px rgba(220, 38, 38, 0.14)',
      padding: '12px 14px',
      animation: 'toastInOut 3s forwards'
    },
    toastIcon: {
      fontSize: '18px',
      lineHeight: 1
    },
    toastTextWrap: {
      display: 'flex',
      flexDirection: 'column',
      gap: '3px'
    },
    toastTitle: {
      fontSize: '13px',
      fontWeight: 700,
      color: '#b91c1c'
    },
    toastText: {
      fontSize: '12px',
      color: '#7f1d1d'
  },
    interviewGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '16px',
      alignItems: 'start'
    },
    leftPane: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      minWidth: 0,
      maxWidth: '960px',
      margin: '0 auto',
      width: '100%'
    },
    rightPane: {
      minWidth: 0,
      position: 'static'
    },
  webcamContainer: {
      width: '460px',
      height: '260px',
      backgroundColor: '#0f172a',
      borderRadius: '24px',
      overflow: 'hidden',
      position: 'fixed',
      right: '22px',
      bottom: '20px',
      zIndex: 45,
      boxShadow: '0 26px 56px -18px rgba(15, 23, 42, 0.52)',
      border: '3px solid rgba(255, 255, 255, 0.92)',
      backdropFilter: 'blur(6px)',
      userSelect: 'none'
  },
  dragHint: {
      position: 'absolute',
      right: '12px',
      bottom: '10px',
      fontSize: '10px',
      fontWeight: 700,
      color: '#cbd5e1',
      backgroundColor: 'rgba(15, 23, 42, 0.62)',
      padding: '4px 8px',
      borderRadius: '999px',
      border: '1px solid rgba(148, 163, 184, 0.35)',
      letterSpacing: '0.04em',
      textTransform: 'uppercase'
  },
  recordingIndicator: {
      position: 'absolute',
      top: '14px',
      left: '14px',
      backgroundColor: 'rgba(15, 23, 42, 0.88)',
      color: '#f8fafc',
      padding: '8px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      letterSpacing: '0.06em',
      backdropFilter: 'blur(4px)',
      border: '1px solid rgba(148, 163, 184, 0.35)'
  },
      resizeHandle: {
        position: 'absolute',
        zIndex: 6,
        background: 'transparent'
      },
      resizeN: {
        top: '-7px',
        left: '10px',
        right: '10px',
        height: '14px',
        cursor: 'ns-resize'
      },
      resizeS: {
        bottom: '-7px',
        left: '10px',
        right: '10px',
        height: '14px',
        cursor: 'ns-resize'
      },
      resizeE: {
        top: '10px',
        bottom: '10px',
        right: '-7px',
        width: '14px',
        cursor: 'ew-resize'
      },
      resizeW: {
        top: '10px',
        bottom: '10px',
        left: '-7px',
        width: '14px',
        cursor: 'ew-resize'
      },
      resizeNE: {
        top: '-8px',
        right: '-8px',
        width: '16px',
        height: '16px',
        cursor: 'nesw-resize'
      },
      resizeNW: {
        top: '-8px',
        left: '-8px',
        width: '16px',
        height: '16px',
        cursor: 'nwse-resize'
      },
      resizeSE: {
        bottom: '-8px',
        right: '-8px',
        width: '16px',
        height: '16px',
        cursor: 'nwse-resize'
      },
      resizeSW: {
        bottom: '-8px',
        left: '-8px',
        width: '16px',
        height: '16px',
        cursor: 'nesw-resize'
      },
  questionCard: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.78) 0%, rgba(248, 251, 255, 0.7) 100%)',
      padding: 'clamp(22px, 3.2vw, 42px) clamp(22px, 3.5vw, 46px)',
      borderRadius: '28px',
      boxShadow: '0 26px 56px rgba(15, 23, 42, 0.09)',
      border: '1px solid rgba(191, 219, 254, 0.8)',
      backdropFilter: 'blur(12px)',
      minHeight: 'min(46vh, 460px)'
  },
  aiBlinker: {
      width: '12px',
      height: '12px',
      background: 'linear-gradient(180deg, #22c55e 0%, #16a34a 100%)',
      borderRadius: '50%',
      marginBottom: '20px',
      boxShadow: '0 0 14px rgba(16, 185, 129, 0.55)'
  },
  questionText: {
      fontSize: 'clamp(24px, 2.9vw, 48px)',
      fontWeight: 700,
      color: '#1e293b',
      lineHeight: 1.34,
      letterSpacing: '-0.015em',
      margin: 0
  },
  controlsArea: {
      marginTop: 'auto',
      paddingTop: '8px',
      paddingBottom: '140px'
  },
  micControlWrapper: {
      background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
      padding: '18px clamp(14px, 2.6vw, 28px)',
      borderRadius: '20px',
      boxShadow: '0 14px 30px rgba(15, 23, 42, 0.05)',
      border: '1px solid #dbeafe',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px'
  },
  statusIndicator: {
      fontSize: '14px',
      textAlign: 'center',
      fontWeight: 600
    },
    statusBadgeAi: {
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: '999px',
      padding: '8px 12px',
      border: '1px solid #c7d2fe',
      background: '#eef2ff',
      color: '#4338ca',
      fontWeight: 700
    },
    statusBadgeListening: {
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: '999px',
      padding: '8px 12px',
      border: '1px solid #fecaca',
      background: '#fff1f2',
      color: '#be123c',
      fontWeight: 700
    },
    statusBadgeProcessing: {
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: '999px',
      padding: '8px 12px',
      border: '1px solid #bae6fd',
      background: '#f0f9ff',
      color: '#0c4a6e',
      fontWeight: 700
    },
    statusBadgeIdle: {
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: '999px',
      padding: '8px 12px',
      border: '1px solid #e2e8f0',
      background: '#f8fafc',
      color: '#475569',
      fontWeight: 700
    },
    wordCounter: {
      fontSize: '12px',
      color: '#64748b',
      marginTop: '-6px',
      textAlign: 'center',
      backgroundColor: '#f8fafc',
      borderRadius: '999px',
      border: '1px solid #e2e8f0',
      padding: '4px 10px'
      },
      transcriptPanel: {
        width: '100%',
        borderRadius: '16px',
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        padding: '10px 12px',
        boxSizing: 'border-box'
      },
      transcriptTitle: {
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: '#64748b',
        fontWeight: 700,
        marginBottom: '8px'
      },
      transcriptBody: {
        minHeight: '52px',
        maxHeight: '120px',
        overflowY: 'auto',
        fontSize: '13px',
        lineHeight: 1.5,
        color: '#334155',
        whiteSpace: 'pre-wrap'
  }
};

export default AIInterviewView;
